/**
 * Suggested variable names for the template Variables whitelist.
 *
 * <p>For a CRON trigger these are authoritative: each {@code
 * RecipientAudience} enum constant on the backend (platform-core's
 * `notification/RecipientAudience.java`) resolves a fixed set of variables
 * per recipient — both audiences today (PTP_EXPIRING_TODAY,
 * PTP_BROKEN_UNRESOLVED) supply exactly the four listed below.</p>
 *
 * <p>For an EVENT trigger there is no such guarantee — {@code
 * NotificationEventDispatcher.dispatch(...)} takes whatever variables map
 * the calling business code passes in, and no business service calls it yet
 * (see the earlier caveat: the event catalog lists every audited event, not
 * every event that's actually wired to fire a notification). The list below
 * is a starting-point suggestion drawn from variable names used elsewhere in
 * the codebase's own templated messages — confirm the real set with
 * engineering once the call site for a given event is implemented.</p>
 */
export type NotificationVariableType = 'string' | 'decimal' | 'date' | 'guid'
export type NotificationVariableStatus = 'live' | 'suggested'

export interface NotificationVariableDef {
  name: string
  type: NotificationVariableType
  status: NotificationVariableStatus
  notes?: string
}

export const AUDIENCE_VARIABLES: Record<string, NotificationVariableDef[]> = {
  PTP_EXPIRING_TODAY: [
    { name: 'customerName', type: 'string', status: 'live' },
    { name: 'loanAccountNo', type: 'string', status: 'live' },
    { name: 'promisedAmount', type: 'decimal', status: 'live' },
    { name: 'promisedDate', type: 'date', status: 'live' },
  ],
  PTP_BROKEN_UNRESOLVED: [
    { name: 'customerName', type: 'string', status: 'live' },
    { name: 'loanAccountNo', type: 'string', status: 'live' },
    { name: 'promisedAmount', type: 'decimal', status: 'live' },
    { name: 'promisedDate', type: 'date', status: 'live' },
  ],
}

/** Not tied to any one event — a generic starting point, not authoritative. */
export const SUGGESTED_EVENT_VARIABLES: NotificationVariableDef[] = [
  { name: 'customerName', type: 'string', status: 'suggested', notes: 'Live for cron audiences; no event call site yet.' },
  { name: 'loanAccountNo', type: 'string', status: 'suggested', notes: 'Live for cron audiences; no event call site yet.' },
  { name: 'mobileE164', type: 'string', status: 'suggested', notes: 'No event call site exists yet.' },
  { name: 'email', type: 'string', status: 'suggested', notes: 'No event call site exists yet.' },
  { name: 'promisedAmount', type: 'decimal', status: 'suggested', notes: 'Live for cron audiences; no event call site yet.' },
  { name: 'promisedDate', type: 'date', status: 'suggested', notes: 'Live for cron audiences; no event call site yet.' },
  { name: 'entityType', type: 'string', status: 'suggested', notes: 'Already live as outbox correlation metadata.' },
  { name: 'entityId', type: 'guid', status: 'suggested', notes: 'Already live as outbox correlation metadata.' },
]
